import React, { ReactNode, useState } from "react";
import { isTourSwitchChoice, QuestionChoice } from "@/lib/tour/steps/v2/question";
import { cn, isStringEmpty, isStringNotEmpty } from "@/lib/utils";
import { DynamicContent } from "./dynamic-content/DynamicContent";
import { ChoiceButtonVariant } from "@common/schemas/v2";
import { UserState } from "@/lib/tour/persistent-storage/userState";
import { CircleCheck, ExpandIcon, Minimize2Icon, Minus, Plus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./Tooltip";
import { SubtitleText } from "@/lib/tour/tourPlayback";
import { Logo } from "./Logo";

interface PromptCardProps {
  title?: string;
  content?: string;
  choices?: ReactNode[];
  inactive?: boolean;
  onWatermarkClick?: () => void;
}

export function PromptCard({ title, content, choices, inactive, onWatermarkClick }: PromptCardProps) {
  if (isStringEmpty(title) && isStringEmpty(content)) {
    return null;
  }

  const hasTitle = isStringNotEmpty(title);
  const hasContent = isStringNotEmpty(content);

  return (
    <div className="pyne-prompt" data-inactive={inactive ? "true" : "false"}>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {hasTitle && <h5 className="pyne-prompt__title">{title}</h5>}
        {hasContent && (
          <div className={cn("pyne-prompt__content", { "pyne-prompt__content--no-title": !hasTitle })}>
            <DynamicContent is="p" content={content} />
          </div>
        )}
        {choices?.length && <div className="pyne-prompt__choices">{choices}</div>}
        <div className="pyne-prompt__label mt-6" onClick={onWatermarkClick} style={{ cursor: "pointer" }}>
          Share feedback — <Logo style={{ width: 48, height: 16, marginTop: "1px" }} />
        </div>
      </div>
    </div>
  );
}

export const ChoiseButton = React.forwardRef<
  HTMLDivElement,
  React.ButtonHTMLAttributes<HTMLDivElement> & { variant?: ChoiceButtonVariant; icon?: ReactNode }
>(({ className, variant, icon, children, ...props }, ref) => {
  return (
    <div
      // type="button"
      className={cn("pyne-prompt__choices__button", className)}
      data-variant={variant}
      ref={ref}
      {...props}
    >
      {icon && <span className="pyne-prompt__choices__icon">{icon}</span>}
      {children}
    </div>
  );
});
ChoiseButton.displayName = "ChoiseButton";

export function SelectPromptCard({
  choices,
  onSelect,
  finishedTourGuideIds = [],
  ...props
}: {
  choices: QuestionChoice[];
  onSelect: (id: string) => void;
  finishedTourGuideIds: UserState["finishedTourGuideIds"];
} & Omit<PromptCardProps, "choices">) {
  const target = React.useRef<HTMLDivElement>(null);

  return (
    <PromptCard
      {...props}
      choices={choices
        .filter((c) => isStringNotEmpty(c.text))
        .map((choice) => {
          const selected = isTourSwitchChoice(choice) && finishedTourGuideIds.includes(choice.guideId);
          const icon = selected ? (
            <CircleCheck width={16} className="pyne-prompt__tour-finished__icon" data-variant={choice.buttonVariant} />
          ) : null;

          return (
            <Tooltip key={choice.id}>
              <TooltipTrigger>
                <ChoiseButton
                  key={choice.id}
                  className={selected ? "pyne-prompt__tour-finished__button" : ""}
                  onClick={() => onSelect(choice.id)}
                  variant={choice.buttonVariant}
                  icon={icon}
                  ref={target}
                >
                  {choice.text}
                </ChoiseButton>
              </TooltipTrigger>
              {selected && <TooltipContent className="pyne-prompt__tour-finished__tooltip">Completed</TooltipContent>}
            </Tooltip>
          );
        })}
    />
  );
}

export function AdditionalContentCard({
  content,
  inactive,
  onWatermarkClick,
}: {
  content: string;
  inactive?: boolean;
  onWatermarkClick?: () => void;
}) {
  if (isStringEmpty(content)) {
    return null;
  }

  const hasContent = isStringNotEmpty(content);

  return (
    <div className="pyne-prompt" data-inactive={inactive ? "true" : "false"}>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {hasContent && <DynamicContent className="pyne-prompt__content" content={content} />}
        <div className="pyne-prompt__label mt-6" onClick={onWatermarkClick} style={{ cursor: "pointer" }}>
          Share feedback — <Logo style={{ width: 48, height: 16, marginTop: "1px" }} />
        </div>
      </div>
    </div>
  );
}

export function ShortClipCard({
  mediaUrl,
  inactive,
  onProceed,
}: {
  mediaUrl: string;
  inactive?: boolean;
  onProceed?: () => void;
}) {
  const [isMinimized, setIsMinimized] = useState(false);

  if (isStringEmpty(mediaUrl)) {
    return null;
  }

  return (
    <div className="pyne-short-clip-prompt-container">
      <div className="pyne-short-clip-prompt" data-inactive={inactive ? "true" : "false"}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <div style={{ color: "#cbd5e1", fontSize: "14px", fontWeight: "500" }}>Proceed after taking the action</div>
            <div
              onClick={() => setIsMinimized(!isMinimized)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#cbd5e1",
                display: "flex",
                padding: "4px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(203, 213, 225, 0.1)";
                e.currentTarget.style.color = "#ffffff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#cbd5e1";
              }}
            >
              {isMinimized ? <ExpandIcon size={16} /> : <Minimize2Icon size={16} />}
            </div>
          </div>

          {!isMinimized && (
            <img
              src={mediaUrl}
              alt="Short clip"
              style={{
                width: "600px",
                objectFit: "contain",
                borderRadius: "8px",
                alignSelf: "flex-start",
              }}
            />
          )}

          <div style={{ alignSelf: "flex-start" }}>
            <ChoiseButton key="proceed" className="pyne-prompt__choices__button" onClick={onProceed} variant="primary">
              Proceed
            </ChoiseButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PromptCardContainer({ children, inactive }: { children: ReactNode; inactive?: boolean }) {
  return (
    <div id="pyne-prompt" className="pyne-prompt-container" data-inactive={inactive ? "true" : "false"}>
      {children}
    </div>
  );
}

export function FixedContentContainer({ children, inactive }: { children: ReactNode; inactive?: boolean }) {
  return (
    <div
      id="pyne-additional-content"
      className="pyne-additional-content-container"
      data-inactive={inactive ? "true" : "false"}
    >
      {children}
    </div>
  );
}

type SubtitleCardProps = { lines: SubtitleText[][]; inactive?: boolean };

export function SubtitlesCard({ inactive, lines }: SubtitleCardProps) {
  const hasContent = Array.isArray(lines) && lines.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <div>
      {hasContent &&
        lines.map((line, index) => {
          const spoken = line.filter(({ isSpoken }) => isSpoken).map(({ word }) => word);
          const unspoken = line.filter(({ isSpoken }) => !isSpoken).map(({ word }) => word);

          return (
            <div key={index} className="pyne-subtitles" data-inactive={inactive ? "true" : "false"}>
              <span style={{ color: "#ffffff" }}>{spoken.join(" ") + " "}</span>
              <span style={{ color: "#bcbcbc" }}>{unspoken.join(" ")}</span>
            </div>
          );
        })}
    </div>
  );
}

export function FixedSubtitlesContainer({ children, inactive }: { children: ReactNode; inactive?: boolean }) {
  return (
    <div id="pyne-subtitles" className="pyne-subtitles-content-container" data-inactive={inactive ? "true" : "false"}>
      {children}
    </div>
  );
}
